'use client';

import { useCommerceCartRoot } from '@components/MiniCart/CommerceCartRoot/CommerceCartRootContext';
import { CommerceQueryProvider } from '@contexts/CommerceQueryProvider';
import { notifyCartOrganizationChanged } from '@lib/commerce/cartChanged';
import {
  countryCodeFromLocale,
  resolveCommerceEnv,
} from '@lib/commerce/commerceEnvironment';
import { useBridgeTokenProvisioning } from '@lib/commerce/useBridgeToken';
import { useCartCurrency } from '@lib/commerce/useCartCurrency';
import { useCartUserId } from '@lib/commerce/useCartUserId';
import { useFollowGuestCart } from '@lib/commerce/useGuestCart';
import { useMultiCart } from '@lib/flags/useMultiCart';
import { createCommerceAxiosClient } from '@lib/http/commerceAxiosClient';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import { resolveActiveOrg } from '@lib/util/resolveActiveOrg';
import {
  CartManagementProvider,
  type CartManagementInitialValues,
} from '@unity/commerce-core';
import { useSession } from 'next-auth/react';
import { useParams } from 'next/navigation';
import { PropsWithChildren, useState } from 'react';
import useSWR from 'swr';

const STORE = 'asset';

const FollowGuestCart = () => {
  useFollowGuestCart();
  return null;
};

type CommerceCartProviderProps = PropsWithChildren<{
  /**
   * The org the server rendered the page for, as its entitlements were
   * (CPS-7696): a client navigation after another tab switched org renders
   * the next page, and so its cart, for the cookie as it is then.
   */
  activeOrganizationId?: string;
}>;

const StandaloneCommerceCartProvider = ({
  activeOrganizationId,
  children,
}: CommerceCartProviderProps) => {
  const currency = useCartCurrency();
  const { data: session, status } = useSession();
  const { enabled: isMultiCartEnabled } = useMultiCart();
  const params = useParams<{ locale: string }>();
  const { isReady: isBridgeTokenReady } = useBridgeTokenProvisioning();
  // CartManagementProvider seeds initialValues into a reducer once, so the
  // client instance can never be swapped after mount (CPS-7658).
  const [client] = useState(() => createCommerceAxiosClient('usg-direct'));
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  // The server falls back to the raw cookie when its org lookup fails, so the
  // rendered org is checked against the same list the header and the drawer
  // resolve against: a stale cookie lands on the same org there and here.
  const { data: orgsData } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId?: string;
  }>(userId ? ['/api/users/organizations', userId] : null, ([url]) =>
    failingFetcher(url)
  );
  const organizationId = resolveActiveOrg(
    activeOrganizationId,
    orgsData?.organizations,
    orgsData?.defaultOrganizationId
  )?.id;
  // Withheld until the bridge token exists: an org id is all
  // OrganizationProvider needs to fire an org bridge call, and a failed mint
  // must not turn that into an unauthenticated request.
  const cartOrganizationId = isBridgeTokenReady ? organizationId : undefined;
  const cartUserId = useCartUserId(userId, cartOrganizationId);

  const initialValues: Omit<
    CartManagementInitialValues,
    'locale' | 'organizationId' | 'userId'
  > = {
    store: STORE,
    client,
    currencyCode: currency,
    countryCode: countryCodeFromLocale(params?.locale),
    env: resolveCommerceEnv(),
    // Start false so no cart is created on PDP load; the hook flips this on
    // only when the user clicks Buy Now.
    enableCartFetching: false,
    ensureCreateAuthenticatedCart: true,
    // A guest's Add to cart builds the guest cart commerce-core keeps
    // (CPS-7693). Seeded once, from the server-rendered session and flags.
    // Never for a signed-in user: while the org is withheld, commerce-core
    // would create a guest cart for them.
    enableAnonymousCart: isMultiCartEnabled && status === 'unauthenticated',
    // Re-price the cart in the active currency on fetch, so a footer currency
    // switch is reflected before checkout.
    syncCartCurrency: true,
    // Keeps the cart read's address, tax and proration checks on for Buy Now;
    // it does not gate CanSubscribe.
    enableCartChecks: true,
    // Reading the cart for this page's org moves it there: other tabs on
    // another org stop reading it (CPS-7696).
    ...(isMultiCartEnabled && {
      onOrganizationChange: notifyCartOrganizationChanged,
    }),
  };

  return (
    <CommerceQueryProvider shareReferenceQueries={isMultiCartEnabled}>
      <CartManagementProvider
        locale={params?.locale}
        userId={cartUserId}
        organizationId={cartOrganizationId}
        initialValues={initialValues}
      >
        <FollowGuestCart />
        {children}
      </CartManagementProvider>
    </CommerceQueryProvider>
  );
};

/** Under the layout's root provider the page shares it; flag off it has its own. */
export const CommerceCartProvider = (props: CommerceCartProviderProps) =>
  useCommerceCartRoot() ? (
    <>{props.children}</>
  ) : (
    <StandaloneCommerceCartProvider {...props} />
  );
